/**
 * ClientDetailDrawer — right-side drawer opened when the owner clicks a client
 * row in the Admin Cost dashboard. Fetches /api/admin/client/{user_id} and
 * displays:
 *   • Basics: org, website, plan, status, signup / trial / renewal / last-login
 *   • Recent reports (last 5) — score + timestamp + delta
 *   • Activity feed (last 10 scans) — trigger + AI cost + timestamp
 *   • Stripe deep-links (customer / subscription) — jump to Stripe if needed
 */
import { useEffect, useState } from "react";
import { X, Loader2, Building2, Globe, Phone, Calendar, Clock, Zap, ExternalLink, FileText, ChevronDown, ChevronRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import ScanIdChip from "./ScanIdChip";

// Normalize whatever Stripe/Mongo/ISO shape we get into a valid JS Date.
// Stripe returns `trial_end` / `current_period_end` as Unix SECONDS
// (10-digit integers). Passing those to `new Date()` treats them as
// milliseconds and yields dates in early 1970 — the exact cause of the
// "Trial ends Jan 21, 1970" bug. Any 10-digit numeric or numeric-string
// value gets promoted to ms before being handed to the Date constructor.
const _toDate = (v) => {
  if (v == null || v === "") return null;
  let n = typeof v === "number" ? v : (typeof v === "string" && /^\d+$/.test(v) ? Number(v) : null);
  if (n != null) {
    // Heuristic: values < 1e12 (before ~Sep 2001 in ms) are seconds.
    if (n < 1e12) n *= 1000;
    const d = new Date(n);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const dt = (v) => {
  const d = _toDate(v);
  return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
};
const dtTime = (v) => {
  const d = _toDate(v);
  return d ? d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
};

export function ClientDetailDrawer({ userId, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  // Lazy-loaded latest report — customer's own dashboard view, on demand.
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    setReport(null);
    setReportOpen(false);
    api.get(`/admin/client/${userId}`)
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch((err) => { if (!cancelled) toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load client"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, userId]);

  const toggleReport = async () => {
    // Lazy-fetch on first expand — keeps the drawer instant to open.
    if (!reportOpen && !report && userId) {
      setReportLoading(true);
      try {
        const { data } = await api.get(`/admin/client/${userId}/report`);
        setReport(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load report");
      } finally {
        setReportLoading(false);
      }
    }
    setReportOpen((v) => !v);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex" data-testid="client-detail-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-black/5 px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Client detail</p>
            <p className="mt-0.5 text-sm font-semibold text-charcoal-900 truncate">
              {detail?.organization_name || detail?.email || "Loading…"}
            </p>
          </div>
          <button onClick={onClose} className="text-charcoal-400 hover:text-charcoal-700 p-1" data-testid="drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading || !detail ? (
          <div className="px-6 py-10 flex items-center gap-2 text-charcoal-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            {/* Basics */}
            <Section title="Organization">
              <Row icon={Building2} label="Name" value={detail.organization_name || "—"} />
              <Row icon={Globe} label="Website" value={detail.website || "—"}
                href={detail.website ? (detail.website.startsWith("http") ? detail.website : "https://" + detail.website) : null} />
              <Row icon={Phone} label="Phone" value={detail.phone || "—"} />
              <Row label="Email" value={detail.email} mono />
            </Section>

            {/* Plan / Dates */}
            <Section title="Plan & billing">
              <Row
                label="Plan"
                value={
                  <span className={"inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full " + (detail.plan === "complete" ? "bg-brand/10 text-brand" : "bg-charcoal-100 text-charcoal-700")}>
                    {detail.plan || "—"}{detail.is_trialing ? " · trial" : ""}
                  </span>
                }
              />
              <Row label="Status" value={detail.subscription_status || "—"} mono />
              <Row icon={Calendar} label="Signed up" value={dt(detail.signup_date)} />
              {detail.subscription_status === "trialing" && detail.trial_end && (
                <Row icon={Clock} label="Trial ends" value={dt(detail.trial_end)} />
              )}
              <Row icon={Calendar} label="Next renewal" value={dt(detail.current_period_end)} />
              <Row icon={Zap} label="Last login" value={dtTime(detail.last_login_at)} />
            </Section>

            {/* Stripe deep-links */}
            {(detail.stripe_customer_id || detail.stripe_subscription_id) ? (
              <Section title="Stripe">
                {detail.stripe_customer_id ? (
                  <a
                    href={`https://dashboard.stripe.com/customers/${detail.stripe_customer_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between px-3 py-2 rounded-lg border border-black/10 hover:bg-charcoal-50 text-sm text-charcoal-700"
                    data-testid="drawer-stripe-customer-link"
                  >
                    <span>Customer <span className="font-mono text-charcoal-400">{detail.stripe_customer_id}</span></span>
                    <ExternalLink className="w-3.5 h-3.5 text-charcoal-400" />
                  </a>
                ) : null}
                {detail.stripe_subscription_id ? (
                  <a
                    href={`https://dashboard.stripe.com/subscriptions/${detail.stripe_subscription_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 flex items-center justify-between px-3 py-2 rounded-lg border border-black/10 hover:bg-charcoal-50 text-sm text-charcoal-700"
                    data-testid="drawer-stripe-subscription-link"
                  >
                    <span>Subscription <span className="font-mono text-charcoal-400">{detail.stripe_subscription_id}</span></span>
                    <ExternalLink className="w-3.5 h-3.5 text-charcoal-400" />
                  </a>
                ) : null}
              </Section>
            ) : null}

            {/* Recent reports */}
            <Section title="Recent reports (5)">
              {detail.recent_reports && detail.recent_reports.length ? (
                <ul className="divide-y divide-black/5 border border-black/10 rounded-lg">
                  {detail.recent_reports.map((r, i) => (
                    <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-charcoal-500 font-mono text-xs">{dtTime(r.created_at)}</span>
                      <span className="flex items-center gap-3">
                        <span className={"font-semibold " + scoreColor(r.score)}>{r.score ?? "—"}<span className="text-charcoal-400 text-xs">/100</span></span>
                        {r.new_issues > 0 ? <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700">+{r.new_issues} new</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-charcoal-400">No reports yet.</p>
              )}
            </Section>

            {/* Latest report — the exact picture the customer sees. Lazy-loaded
                on expand so the drawer opens instantly. Uses the read-side
                graft in /admin/client/{uid}/report so the operator sees the
                same donation_pages_watched / monitoring_reach the customer's
                own dashboard shows. */}
            <Section title="Customer's latest report (their view)">
              <button
                type="button"
                onClick={toggleReport}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-black/10 hover:bg-charcoal-50 text-sm text-charcoal-700"
                data-testid="drawer-report-toggle"
              >
                <span className="inline-flex items-center gap-1.5">
                  {reportOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  <FileText className="w-3.5 h-3.5 text-charcoal-400" />
                  {reportOpen ? "Hide latest report" : "View latest report"}
                </span>
                {reportLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-charcoal-400" /> : null}
              </button>
              {reportOpen && report && !reportLoading ? (
                <ClientReportPanel report={report.report} createdAt={report.created_at} />
              ) : null}
              {reportOpen && !report && !reportLoading ? (
                <p className="mt-2 text-sm text-charcoal-400 px-3">No report on file yet.</p>
              ) : null}
            </Section>

            {/* Activity */}
            <Section title="Activity (last 10 scans)">
              {detail.activity && detail.activity.length ? (
                <ul className="divide-y divide-black/5 border border-black/10 rounded-lg">
                  {detail.activity.map((a, i) => (
                    <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-charcoal-500 font-mono text-xs">{dtTime(a.run_at)}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-[11px] font-mono uppercase tracking-widest text-charcoal-500">{a.trigger}</span>
                        <span className="text-charcoal-400 text-xs font-mono">${(a.ai_cost || 0).toFixed(4)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-charcoal-400">No scan activity recorded.</p>
              )}
            </Section>
          </div>
        )}
      </aside>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-2">{title}</p>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Row({ icon: Icon, label, value, href, mono }) {
  const body = (
    <span className={"text-sm text-charcoal-800 " + (mono ? "font-mono text-xs" : "")}>{value}</span>
  );
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="flex items-center gap-1.5 text-[13px] text-charcoal-500 min-w-0 shrink-0">
        {Icon ? <Icon className="w-3.5 h-3.5 text-charcoal-400" strokeWidth={2} /> : null}
        {label}
      </span>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-brand hover:underline truncate max-w-[240px]">{value}</a>
      ) : body}
    </div>
  );
}

function scoreColor(score) {
  if (score == null) return "text-charcoal-500";
  if (score >= 90) return "text-emerald-600";
  if (score >= 70) return "text-amber-600";
  return "text-red-600";
}

/**
 * ClientReportPanel — compact read-only view of the customer's most-recent
 * stored health report. Shows the same key numbers the customer sees:
 *   • Overall score + scan_depth + when it ran
 *   • The `monitoring_reach` detail (total pages, coverage tier)
 *   • The `donation_pages_watched` detail (# donation forms) with a
 *     collapsible URL list
 *   • Every currently-red or currently-amber check, with the raw detail
 *     string — that's what the customer will point to when they email
 *     support asking "why did my score drop?"
 * Rendered ONLY inside the admin drawer; never shipped as a customer surface.
 */
function ClientReportPanel({ report, createdAt }) {
  const [expandedItems, setExpandedItems] = useState(false);
  if (!report) return <p className="mt-2 text-sm text-charcoal-400 px-3">Report is empty.</p>;

  const checks = report.checks || [];
  const byKey = Object.fromEntries(checks.map((c) => [c.key, c]));
  const reach = byKey.monitoring_reach;
  const watched = byKey.donation_pages_watched;
  const problems = checks.filter((c) => c.status === "red" || c.status === "amber");
  const dtShort = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined,
      { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return "—"; }
  };

  return (
    <div className="mt-2 rounded-lg border border-black/10 bg-charcoal-50/50 px-3 py-3 space-y-3 text-sm">
      <div className="flex items-center justify-between text-[11px] font-mono text-charcoal-500">
        <span>
          <span className={"font-semibold " + scoreColor(report.overall_score)}>{report.overall_score ?? "—"}</span>
          <span className="text-charcoal-400">/100</span>
          <span className="ml-2 uppercase tracking-widest">{report.scan_depth || "basic"}</span>
        </span>
        <span>{dtShort(createdAt)}</span>
      </div>
      {report.scan_id && (
        <div className="-mt-1" data-testid="admin-scan-id">
          <ScanIdChip scanId={report.scan_id} showCopy testid="admin-scan-id-chip" />
        </div>
      )}

      {reach ? (
        <div className="rounded-md bg-white border border-black/5 px-2.5 py-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-0.5">
            Total Site Pages Discovered
          </div>
          <div className="text-charcoal-800 text-xs leading-snug">{reach.detail}</div>
        </div>
      ) : null}

      {watched ? (
        <div className="rounded-md bg-white border border-black/5 px-2.5 py-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-0.5">
                Donation Forms Being Watched{watched.carried_forward ? " · carried forward" : ""}
              </div>
              <div className="text-charcoal-800 text-xs leading-snug">{watched.detail}</div>
            </div>
            {watched.items && watched.items.length ? (
              <button
                type="button"
                onClick={() => setExpandedItems((v) => !v)}
                className="text-[11px] font-mono text-brand hover:underline shrink-0 ml-2"
                data-testid="drawer-watched-toggle"
              >
                {expandedItems ? "Hide" : `${watched.items.length} URLs`}
              </button>
            ) : null}
          </div>
          {expandedItems && watched.items ? (
            <ul className="mt-2 max-h-52 overflow-y-auto space-y-0.5">
              {watched.items.map((it, i) => (
                <li key={i} className="text-[11px] font-mono text-charcoal-600 break-all">
                  <a href={it.url} target="_blank" rel="noreferrer" className="hover:underline">
                    {it.url}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {problems.length ? (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-1">
            Open issues ({problems.length})
          </div>
          <ul className="space-y-1.5">
            {problems.map((c) => (
              <li key={c.key} className="rounded-md bg-white border border-black/5 px-2.5 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-charcoal-900 text-xs font-medium">{c.label}</span>
                  <span className={
                    "text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full " +
                    (c.status === "red"
                      ? "bg-red-50 text-red-700"
                      : "bg-amber-50 text-amber-700")
                  }>
                    {c.status}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-charcoal-600 leading-snug">{c.detail}</div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-xs text-emerald-700 font-mono">No open issues.</div>
      )}
    </div>
  );
}
